import Constant from "@/common/constant/Constant";
import { isEditField } from "../../handler/CommonHandler";
import Utils from "@/common/utils/utils";
import { updateFormField } from "@/service/formManagementService";
import trI18n from '@/common/utils/trI18n';
import { trTag } from '@/common/utils/tr';

/**
 * A tag item added on screen but not saved yet (temporary id).
 * @param {Object} item tag item
 * @returns {boolean}
 */
export const isTemporaryTagItem = item => !!item?.id?.includes(Constant.TAG_ITEM_TEMP_ID_SUFFIX);

/**
 * Saved tag names, built from the list last loaded from the server (the same baseline the screen uses
 * to detect unsaved changes).
 * @param {Array} savedItemList saved tag items
 * @returns {{ nameById: Map<string, string>, nameSet: Set<string> }}
 */
export const buildSavedTagNames = savedItemList => {
    const nameById = new Map();
    const nameSet = new Set();
    (savedItemList || []).forEach(item => {
        if (!item?.id || isTemporaryTagItem(item)) return;
        nameById.set(item.id, item.name);
        nameSet.add(item.name);
    });
    return { nameById, nameSet };
};

/**
 * Tag item name to display.
 * Only a saved tag with its saved name goes through trTag. A temporary tag, or a tag renamed but not
 * saved yet, would be recorded as a MISS and sent to translation before the user submits it.
 * @param {Object} item tag item
 * @param {{ nameById: Map<string, string> }} savedTagNames result of buildSavedTagNames
 * @returns {string}
 */
export const getTagItemDisplayName = (item, savedTagNames) => {
    const isSavedName = !isTemporaryTagItem(item) && savedTagNames?.nameById?.get(item?.id) === item?.name;
    return isSavedName ? trTag(item?.name) : item?.name;
};

/**
 * Tag name (text only, e.g. in the duplicate name popup) to display.
 * Translated only when it is the name of a saved tag.
 * @param {string} name tag name
 * @param {{ nameSet: Set<string> }} savedTagNames result of buildSavedTagNames
 * @returns {string}
 */
export const getTagNameDisplay = (name, savedTagNames) => (savedTagNames?.nameSet?.has(name) ? trTag(name) : name);

export const checkIsCancelTabs = (argsAll) => {
    switch (argsAll.settingSelectItem) {
        case Constant.SETTING_SELECT_ITEM_TAG_FIELD.MASTER_KNOWLEDGE:
            // check master knowledge
            if (
                argsAll.currentUpdateField?.relatedMasterForm !== argsAll.masterKnowledge.formMaster ||
                argsAll.currentUpdateField?.settingAutoAdd !== argsAll.masterKnowledge.isAutoAddData
            ) {
                break;
            }

            // check update item
            let isUpdateItem = false;
            for (const newItem of argsAll.masterKnowledge.listItem) {
                let currentItem = argsAll.currentUpdateField?.structure?.listItem?.find(
                    o => o.itemId === newItem.itemId
                );
                if (
                    newItem.isActive !== currentItem?.isActive ||
                    newItem.order !== currentItem?.order
                ) {
                    isUpdateItem = true;
                    break;
                }
            }

            if (!isUpdateItem) {
                return false;
            }
            break;
        case Constant.SETTING_SELECT_ITEM_TAG_FIELD.AUTO_FILL:
            // check master knowledge
            if (
                argsAll.currentUpdateField?.relatedMasterForm !==
                argsAll.autoFillMasterField.formMaster ||
                    argsAll.currentUpdateField?.settingAutoAdd !==
                    argsAll.autoFillMasterField.isAutoAddData ||
                    argsAll.currentUpdateField?.relatedField !== argsAll.autoFillMasterField.relatedField
            ) {
                break;
            }

            // check update item
            let isUpdateAutoFillItem = false;
            for (const newItem of argsAll.autoFillMasterField.listItem) {
                let currentItem = argsAll.currentUpdateField?.structure?.listItem?.find(
                    o => o.itemId === newItem.itemId
                );
                if (
                    newItem.isActive !== currentItem?.isActive ||
                    newItem.order !== currentItem?.order
                ) {
                    isUpdateAutoFillItem = true;
                    break;
                }
            }

            if (!isUpdateAutoFillItem) {
                return false;
            }
            break;
        default:
            // if not change data then go to screen parent
            if (argsAll.isDisableBtnSave && argsAll.defaultData?.itemList?.length === argsAll.itemList?.length) {
                return false;
            }
            break;
    }
    return true;
};

export const isHiddenEditBtn = (argsCommon) => {
    if (
        [Constant.FORM_MANAGEMENT_SCREEN.EDIT_FORM].includes(argsCommon.parentPanelScreen) &&
        !isEditField(argsCommon.currentUpdateField, argsCommon)
    ) {
        return true;
    }
    return false;
};

/**
 * isDisableEditTagFieldName
 * disable edit field name in tag item list when isHiddenEditBtn or in add form screen
 * @param {*} props 
 * @returns 
 */
export const isDisableEditTagFieldName = (props) => {
    return isHiddenEditBtn(props) || props.parentPanelScreen === Constant.FORM_MANAGEMENT_SCREEN.ADD_FORM;
}

export const isHiddenToolsTagItems = argsCommon => {
    const isDifferentAffiliation =
        argsCommon.currentUpdateField.affiliationProject !== argsCommon.projectDetail.id;

    const isEditFormScreen =
        Constant.FORM_MANAGEMENT_SCREEN.EDIT_FORM === argsCommon.parentPanelScreen;

    const isNotEditable =
        isEditFormScreen && !isEditField(argsCommon.currentUpdateField, argsCommon);

    return isDifferentAffiliation || isNotEditable;
};

export const validateDuplicateItemName = async (itemList, currentUpdateField) => {
    const isEditTagItem = Utils.getPrefixOfId(currentUpdateField) === Constant.ID_PREFIX.FIELD;
    // Add new tag field => check duplicate name in list item in frontend
    if (!isEditTagItem) {
        const duplicateNames = [];
        const nameCount = {};
        
        itemList.forEach(item => {
            if (item.name) {
                const trimmedName = item?.name?.trim();
                if (trimmedName) {
                    nameCount[trimmedName] = (nameCount[trimmedName] || 0) + 1;
                }
            }
        });
        
        Object.keys(nameCount).forEach(name => {
            if (nameCount[name] > 1) {
                duplicateNames.push(name);
            }
        });

        return duplicateNames;
    }

    // Edit tag item list => Always validate in backend
    if (isEditTagItem) {
        const requestData = {
            method: Constant.FORM_FIELD_MANAGEMENT_ACTION_FLAG.VALIDATE_DUPLICATE_TAG_ITEM,
            formEditId: currentUpdateField,
            itemList: JSON.stringify(itemList),
        }
        const validateResult = await updateFormField(null, requestData);
        if (!validateResult?.status) {
            let duplicateList = Utils.convertJSONDataToObject(validateResult?.fieldEditItemData);
            if (!Utils.isEmptyArray(duplicateList)) {
                return duplicateList;
            }
        }
        return [];
    }

    return [];
}

/**
 * Validates and parses keywords from search input
 * @param {string} keyword - Raw keyword input
 * @returns {string[]|null} - Array of valid keywords or null if invalid
 */
export const validateAndParseKeywords = (keyword) => {
    const keywordTrim = keyword.trim();
    if (Utils.isEmptyString(keywordTrim)) {
        return null;
    }

    const keywords = keywordTrim.split(' ').filter(k => !Utils.isEmptyString(k));
    if (Utils.isEmptyArray(keywords)) {
        return null;
    }

    return keywords;
};

/**
 * Formats search results to tag item structure
 * @param {Array} searchResults - Raw search results from API
 * @returns {Array} - Formatted tag items
 */
export const formatSearchResultsToTagItems = (searchResults) => {
    // Handle edge cases: null, undefined, or empty array
    if (!searchResults || !Array.isArray(searchResults) || Utils.isEmptyArray(searchResults)) {
        return [];
    }

    return searchResults
        .filter(item => item && item.id) // Filter out invalid items
        .map(item => {
            const structure = Utils.convertJSONDataToObject(item.structure) || {};
            const itemName =
                typeof Utils.convertJSONDataToObject(item.name) === 'string'
                    ? item.name
                    : Utils.getTextByLanguage(
                          Constant.LANGUAGE_TYPE.JP,
                          Utils.convertJSONDataToObject(item.name) || {}
                      );
            return {
                // Core identification fields
                id: item.id,
                usage: item.usage || Constant.FORM_USAGE.TAG, // CRITICAL: Partition key for DynamoDB
                
                // Affiliation fields
                affiliationOrganization: item.affiliationOrganization,
                affiliationProject: item.affiliationProject,
                affiliationField: item.affiliationField,
                
                // User fields
                creator: item.creator,
                updator: item.updator,
                
                // Name (formatted for display)
                name: itemName || '',
                
                // Structure (contains isShowItemInTicket, no, etc.)
                structure: structure,
                
                // Metadata fields
                type: item.type || '',
                definition: item.definition,
                explanation: item.explanation,
                viewForCustomer: item.viewForCustomer || JSON.stringify({}),
                usingProjects: item.usingProjects || [],
                
                // Numeric fields
                hitNumber: item.hitNumber || 0,
                no: structure?.no || 0,
                
                // Timestamps
                createdAt: item?.createdAt || '',
                updatedAt: item?.updatedAt || '',
                
                // UI-only fields (not stored in DB)
                isCheckbox: structure?.isShowItemInTicket || false,
                isEditName: false,
                isErrorValidate: false,
                errorMsg: '',
                isAutoFocus: false
            };
        });
};

/**
 * Handles search input change event
 * @param {Event} event - Input change event
 * @param {Function} setSearchKeyword - State setter for search keyword
 */
export const handleSearchInputChange = (event, setSearchKeyword) => {
    const value = event.target.value;
    setSearchKeyword(value);
};

/**
 * Handles search key press event (Enter key)
 * @param {Event} event - Key press event
 * @param {string} searchKeyword - Current search keyword value
 * @param {Function} handleSearchTagItems - Function to handle search tag items
 */
export const handleSearchKeyPress = (event, searchKeyword, handleSearchTagItems) => {
    if (event.key === 'Enter') {
        handleSearchTagItems(searchKeyword);
    }
};

/**
 * Handles search icon click event
 * @param {string} searchKeyword - Current search keyword value
 * @param {Function} handleSearchTagItems - Function to handle search tag items
 */
export const handleSearchIconClick = (searchKeyword, handleSearchTagItems) => {
    handleSearchTagItems(searchKeyword);
};

/**
 * Processes search results and validates them
 * @param {Array} searchResults - Raw search results from API
 * @param {Function} setSearchError - State setter for search error message
 * @returns {Array|null} - Formatted tag items or null if validation fails
 */
export const processSearchResults = (searchResults, setSearchError) => {
    let errorMessage = '';

    if (Utils.isEmptyArray(searchResults)) {
        errorMessage = trI18n('MSG-TAG-ITEM-SEARCH-EMPTY-RESULT');
        setSearchError(errorMessage);
        return null;
    }

    if (
        !Utils.isEmptyArray(searchResults) &&
        searchResults[0]?.totalItem > Constant.ITEMS_PER_QUERY_LIMIT
    ) {
        errorMessage = trI18n('MSG-TAG-ITEM-SEARCH-OVER-RESULT');
    }
    setSearchError(errorMessage);
    return formatSearchResultsToTagItems(searchResults);
};